
'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from '@/navigation';
import Image from 'next/image';

export default function ProjectsPage() {
  const t = useTranslations('projects');

  const projects = [
    {
      industry: t('fashion.badge'),
      title: t('fashion.title'),
      bgImage: "/luxe.png",
      href: "https://hvn-ecommerce-luxurybrand.vercel.app/"
    },
    {
      industry: t('education.badge'),
      title: t('education.title'),
      bgImage: "/learnify.png",
      href: "https://hvn-education-platform.vercel.app/"
    },
    {
      industry: t('saas.badge'),
      title: t('saas.title'),
      bgImage: "/cloudflow.png",
      href: "https://hvn-modernsaasplatform.vercel.app/"
    },
    {
      industry: t('agency.badge'),
      title: t('agency.title'),
      bgImage: "/apex.png",
      href: "https://hvn-creative-agency.vercel.app/"
    }
  ];

  return (
    <main className="min-h-screen bg-white dark:bg-black">
      <Navbar />
      
      <section className="py-24 md:py-40">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mb-24">
            <h1 className="text-6xl md:text-[clamp(64px,8vw,120px)] font-sans font-bold tracking-tighter mb-8 dark:text-white leading-[0.9]">
              {t('title')}
            </h1>
            <p className="text-xl text-gray-500 dark:text-gray-400">
              {t('subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, i) => {
              const isExternal = project.href.startsWith('http');
              
              return (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.02 }}
                  className="group relative aspect-[16/10] rounded-3xl overflow-hidden cursor-pointer bg-gray-100 dark:bg-zinc-900"
                >
                  {isExternal ? (
                    <a href={project.href} target="_blank" rel="noopener noreferrer" aria-label={`View project: ${project.title}`} className="absolute inset-0 z-10" />
                  ) : (
                    <Link href={project.href} aria-label={`View project: ${project.title}`} className="absolute inset-0 z-10" />
                  )}
                  
                  <div className="absolute inset-0 flex items-center justify-center transition-opacity">
                     <Image 
                       src={project.bgImage} 
                       alt={project.title} 
                       fill 
                       className="object-cover transition-transform duration-700 group-hover:scale-105"
                       priority={i < 2}
                     />
                  </div>

                  <div className="absolute top-8 left-8 z-20">
                    <span className="px-3 py-1 bg-white/40 backdrop-blur-md rounded-full text-[10px] font-mono font-bold uppercase tracking-wider text-black border border-white/20">
                      {project.industry}
                    </span>
                  </div>
                  
                  <div className="absolute bottom-8 left-8 right-8 z-20 flex items-center justify-between p-6 bg-white/95 backdrop-blur-sm rounded-2xl dark:bg-zinc-900/95 transition-transform group-hover:translate-y-[-8px] shadow-lg">
                    <div>
                      <h3 className="text-xl font-bold text-black dark:text-white">{project.title}</h3>
                    </div>
                    <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center dark:bg-white dark:text-black">
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
